import { useState } from "react";
import { Button, Popconfirm, Space, Table, Tag, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  MedicinePublication,
  MedicinePublicationFormValues,
  MedicinePublicationKind,
  PUBLICATION_LABELS,
} from "./types";
import { MedicinePublicationModal } from "./MedicinePublicationModal";
import { useMedicinePublications } from "./useMedicinePublications";

type MedicinePublicationsTableProps = {
  kind: MedicinePublicationKind;
};

/**
 * Блок «Статьи» или «Тезисы» на странице материалов (12440). Компонент один,
 * различается только подписями и параметром kind в запросах.
 */
export const MedicinePublicationsTable = ({
  kind,
}: MedicinePublicationsTableProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MedicinePublication>();
  const labels = PUBLICATION_LABELS[kind];
  const {
    loading,
    publications,
    removingIds,
    reordering,
    saving,
    movePublication,
    removePublication,
    savePublication,
  } = useMedicinePublications(kind);

  const openCreateModal = () => {
    setEditing(undefined);
    setModalOpen(true);
  };

  const openEditModal = (publication: MedicinePublication) => {
    setEditing(publication);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(undefined);
  };

  const handleSave = async (values: MedicinePublicationFormValues) => {
    await savePublication(values);
    closeModal();
  };

  return (
    <>
      <Table<MedicinePublication>
        title={() => (
          <Space>
            <strong>{labels.blockTitle}</strong>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              {labels.addButton}
            </Button>
          </Space>
        )}
        dataSource={publications}
        loading={loading}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: <div style={{ margin: 56 }}>{labels.emptyText}</div>,
        }}
      >
        <Table.Column<MedicinePublication>
          width={90}
          key="order"
          title="Порядок"
          render={(_, record, index) => (
            <Space size={4}>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0 || reordering}
                onClick={() => movePublication(record.id, -1)}
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === publications.length - 1 || reordering}
                onClick={() => movePublication(record.id, 1)}
              />
            </Space>
          )}
        />
        <Table.Column<MedicinePublication>
          dataIndex="title"
          key="title"
          title="Заголовок"
          render={(value, record) =>
            record.linkUrl ? (
              <Typography.Link
                href={record.linkUrl}
                target="_blank"
                rel="noreferrer"
              >
                {value}
              </Typography.Link>
            ) : (
              <Typography.Text>{value}</Typography.Text>
            )
          }
        />
        <Table.Column<MedicinePublication>
          dataIndex="authors"
          key="authors"
          title="Авторы"
          width={260}
        />
        <Table.Column<MedicinePublication>
          dataIndex="source"
          key="source"
          title="Источник"
          width={280}
          render={(value) =>
            value || <Typography.Text type="secondary">—</Typography.Text>
          }
        />
        <Table.Column<MedicinePublication>
          dataIndex="publishedYear"
          key="publishedYear"
          title="Год"
          width={80}
          render={(value) => value || "—"}
        />
        <Table.Column<MedicinePublication>
          dataIndex="isActive"
          key="isActive"
          title="На сайте"
          width={110}
          render={(value) =>
            value ? <Tag color="green">Показывается</Tag> : <Tag>Скрыта</Tag>
          }
        />
        <Table.Column<MedicinePublication>
          width={220}
          fixed="right"
          key="actions"
          title="Действия"
          render={(_, record) => (
            <Space size={8} direction="vertical" style={{ width: "100%" }}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
                block
              >
                Редактировать
              </Button>
              <Popconfirm
                title={labels.removeConfirm}
                okText="Удалить"
                cancelText="Отмена"
                onConfirm={() => removePublication(record.id)}
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={removingIds.includes(record.id)}
                  block
                >
                  Удалить
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <MedicinePublicationModal
        open={modalOpen}
        saving={saving}
        kind={kind}
        publication={editing}
        onCancel={closeModal}
        onSubmit={handleSave}
      />
    </>
  );
};
